import { useMemo } from 'react'
import { Stack, Text } from '@mantine/core'
import type { ClaimPresentPositions } from '@api-sdk'
import { Select } from '../../../Common/Select'
import { Textarea } from '../../../Common/Textarea'
import { StatusMessage } from '../../../Common/StatusMessage'
import { PRESENT_POSITION_NOTES_MAX_LENGTH } from '../../../../hooks/usePresentPositionEdit'

export interface PresentPositionEditorProps {
  // Selected option id (TODomainInstance id) — the backend saves by id, not label.
  value: number | null
  description: string | null
  onValueChange: (next: number | null) => void
  onDescriptionChange: (next: string | null) => void
  options: ClaimPresentPositions[] | null
  optionsLoading?: boolean
  optionsError?: string | null
  // Why the description is invalid, owned by usePresentPositionEdit. Shown here, next to
  // the field it belongs to, and it is the only place the message appears (MSD-736).
  validationError?: string | null
  disabled?: boolean
}

// Mantine Select works with string values, so option ids are stringified for
// display and parsed back to numbers on change.
function toSelectData(options: ClaimPresentPositions[] | null): { value: string; label: string }[] {
  if (!options) return []
  return options
    .filter((o): o is ClaimPresentPositions & { claimPresentPosition: string } => {
      return o.claimPresentPosition != null && o.claimPresentPosition !== '' && o.id != null
    })
    .map((o) => ({ value: String(o.id), label: o.claimPresentPosition }))
}

export function PresentPositionEditor({
  value,
  description,
  onValueChange,
  onDescriptionChange,
  options,
  optionsLoading,
  optionsError,
  validationError,
  disabled,
}: Readonly<PresentPositionEditorProps>) {
  const data = useMemo(() => toSelectData(options), [options])
  const placeholder = optionsLoading ? 'Loading positions…' : 'Select position'
  const descriptionLength = description?.length ?? 0
  const atMaxLength = descriptionLength >= PRESENT_POSITION_NOTES_MAX_LENGTH

  return (
    <Stack gap="xs">
      <Select
        aria-label="Present Position"
        size="xs"
        data={data}
        value={value == null ? null : String(value)}
        onChange={(next) => onValueChange(next == null ? null : Number.parseInt(next, 10))}
        placeholder={placeholder}
        disabled={disabled || optionsLoading}
        clearable
      />
      {optionsError && (
        <StatusMessage status="error" fz="xs">
          {optionsError}
        </StatusMessage>
      )}
      <Textarea
        label="Description"
        size="xs"
        autosize
        minRows={3}
        maxLength={PRESENT_POSITION_NOTES_MAX_LENGTH}
        value={description ?? ''}
        onChange={(event) => onDescriptionChange(event.currentTarget.value || null)}
        placeholder="Add description and other notes"
        disabled={disabled}
      />
      <Text fz="xs" ta="right" c={atMaxLength ? 'var(--mantine-color-error)' : 'text.2'}>
        {descriptionLength} / {PRESENT_POSITION_NOTES_MAX_LENGTH} characters
      </Text>
      {validationError && (
        <StatusMessage status="error" fz="xs">
          {validationError}
        </StatusMessage>
      )}
    </Stack>
  )
}
